import * as angular from 'angular';
import * as d3 from 'd3';
import * as _ from 'lodash';

import { IGroupService, ILoggerService, IUserSettingsService, ICurrentApplicationService, IApplicationService, IAuthenticationService } from '../../services';
import { ITreeItem, IdentifierType, Group, GroupConfigMode, ComponentDefinition, ObjectUser, GroupTreeViewModel } from '../../models';
import { Helper } from '../../common/helpers';
import { IRootScope } from '../../IRootScope';
import { guidEmpty } from '../../common/constants';
import { groupAdded, groupSelected, groupTreeChanged } from '../../common';
import { IModelStructureMenu } from '../../states/services.index';

class SimpleIdentifier {
    name: string;
    type: IdentifierType;
}

class SexyModelTreeController implements angular.IComponentController {
    changeCount: { [guid: string]: number };
    checkDeleteGroup: (value: { groupGuid: string }) => void;
    displayedGroups: any[];
    exposedScope: any;
    filteredGroups: any[]; // to expose outside
    formController: angular.IFormController;
    groups: GroupTreeViewModel[];
    hasMore: boolean;
    isCreateChildApplicationAllowed: boolean = false;
    isEditMode: boolean;
    isLoading: boolean;
    menu: IModelStructureMenu[];
    nameStyle: any;
    selectedGroup: any;
    templateGuid: string;
    usages: ObjectUser[];

    private _nameWidth = 3;
    private _buttonsWidth = 0.13;
    private _statusWidth = 0.15;

    static $inject = [
        '$scope',
        '$rootScope',
        '$q',
        '$translate',
        '$element',
        '$timeout',
        'groupService',
        'loggerService',
        'currentApplicationService',
        'applicationService',
        'authenticationService',
        'userSettingsService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _$q: angular.IQService,
        private _$translate,
        private _$element: angular.IAugmentedJQuery,
        private _$timeout: angular.ITimeoutService,
        private _groupService: IGroupService,
        private _loggerService: ILoggerService,
        private _currentApplicationService: ICurrentApplicationService,
        private _applicationService: IApplicationService,
        private _authenticationService: IAuthenticationService,
        private _userSettingsService: IUserSettingsService
    ) {
        this.nameStyle = {
            'width': 10 / 12 * 100 + '%',
            'flex-basis': 10 / 12 * 100 + '%'
        };
    }

    private _watchers;

    $onInit() {
        this.checkCreateChildApplicationAllowed();
        const menu: any[] = this._userSettingsService.getApplicationSetting<any[]>('modelMenu');
        if (this.menu) {
            this.menu.forEach(m => {
                const found = _.find(menu, mm => mm.label === m.label);
                if (found) {
                    m.model.isVisible = found.model.isVisible;
                }
            });
        }

        this._watchers = [
            this._$scope.$watch(() => this.menu, () => {
                this.updateStyles();
                this._userSettingsService.setApplicationSetting('modelMenu', this.menu);
            }, true),
            this._$scope.$watch(() => this.isEditMode, () => {
                this.updateStyles();
            }),
            this._$scope.$watch(() => this.displayedGroups, () => {
                this.filteredGroups = this.displayedGroups
                    ? this.displayedGroups.filter(g => g.guid !== guidEmpty)
                    : [];
                try {
                    const container = this._$element.find('.model-tree-container');
                    const found = this.selectedGroup
                        ? _.find(this.displayedGroups, d => d.guid === this.selectedGroup.guid)
                        : null;
                    if (found) {
                        this.bringIntoView(found);
                    } else {
                        container.scrollTop(0);
                    }
                } catch (e) {
                    console.error(e);
                }
            })
        ];
    }

    $onDestroy() {
        try {
            if (this._watchers && this._watchers.length) {
                this._watchers.forEach(w => w());
            }
        } catch (e) {
            console.error(e);
        }
    }

    private bringIntoView(group: Group) {
        const container = this._$element.find('.model-tree-container');
        const rowHeight = container
            .find('.model-row')
            .first()
            .innerHeight();
        const expectedPos = rowHeight * (this.displayedGroups.indexOf(group));
        // Needs a timeout to wait for render
        this._$timeout(() => {
            container.scrollTop(expectedPos);
        });
    }

    private checkCreateChildApplicationAllowed(): void {
        const currentApplicationConfiguration = this._currentApplicationService.getApplicationConfiguration();
        const authentication = this._authenticationService.authentication;
        const userName = authentication.userName;
        this._applicationService
            .getOneWithInformation(userName, currentApplicationConfiguration.guid)
            .then(data => {
                this.isCreateChildApplicationAllowed = data.item1.isChildApplicationCreationAllowed;
            })
            .catch(error => this._loggerService.logError(error));
    }

    private updateStyles() {
        if (this.menu) {
            const visible: any[] = this.menu.filter(d => d.label !== 'Steps' && d.model.isVisible);

            const visibleCount = d3.sum(visible, v => v.width) + this._nameWidth;
            visible.forEach(v => {
                v.style = this.getStyle(v.width, visibleCount);
            });

            this.nameStyle = this.getStyle(this._nameWidth, visibleCount);
        }
    }

    private getStyle(width: number, visibleCount: number) {
        let available = 1 - this._statusWidth;
        if (this.isEditMode) {
            available -= this._buttonsWidth;
        }
        const value = width / visibleCount * 100 * available;
        return {
            'width': value + '%',
            'flex-basis': value + '%'
        };
    }

    private buildArray(f: ITreeItem, currentList: string[], property: string): string[] {
        currentList.push(f[property]);
        if (f.children) {
            f.children.forEach(c => this.buildArray(c, currentList, property));
        }
        return currentList;
    }

    addChild(group: Group & { depth: number }, data) {
        if (group) {
            const itemId = data.itemId;
            switch (itemId) {
                case 'group':
                    this.createGroup(group, '', []);
                    break;
                case 'redundancyGroup':
                    const identifier: SimpleIdentifier = { name: 'Result', type: IdentifierType.CalculatedValue };
                    this.createGroup(group, 'Redundancy', [identifier]);
                    break;
                default:
                    if (_.startsWith(itemId, 'template')) {
                        const guid = itemId.substring(8);
                        this.templateGuid = guid;
                        this.selectedGroup = group;
                        angular.element('#createFromTemplateModal').modal('show');
                    } else {
                        this.updateExisting(itemId, group);
                    }
                    break;
            }
        }
    }

    select(group, $event: angular.IAngularEvent) {
        if (this.formController && this.formController.$dirty) {
            this._loggerService.logWarning('Save or discard changes before unselecting.');
            return;
        }

        if (!group || !this.isRoot(group)) {
            this.selectedGroup = group;
            this._$rootScope.$broadcast(groupSelected, this.selectedGroup);
        }
        $event.preventDefault();
    }

    isRoot(group: Group) {
        return group.guid === guidEmpty;
    }

    createNewEmptyGroup(parent: Group, groupType?: string, identifiers?: SimpleIdentifier[], depth?: number): Group & { depth: number } {
        if (!groupType) {
            groupType = '';
        }
        const group: any = {
            parentGUID: parent.guid,
            name: '',//this.getGroupUniqueName(this.groups, 'New group'),
            guid: Helper.getGuid(),
            depth: depth,
            type: groupType,
            isCreating: true,
            configMode: GroupConfigMode.ByGroup,
            identifierCollection: identifiers
                ? identifiers.map(i => {
                    return {
                        name: this._$translate.instant(i.name),
                        type: i.type
                    };
                })
                : [],
            parent: parent
        };
        return group;
    }

    private createGroup(parent, groupType: string, identifiers?: SimpleIdentifier[]) {
        const group = this.createNewEmptyGroup(parent, groupType, identifiers, parent.depth + 1);
        if (!parent.children) {
            parent.children = [];
        }
        this.addGroup(group, parent, null);
    }

    addGroup(group: Group & { depth: number }, parent: any, _$event: angular.IAngularEvent) {
        this.selectedGroup = group;
        this.addUnderParentInTree(this.displayedGroups, parent.guid, group);
        let index = this.groups.map(g => g.guid).indexOf(parent.guid);
        this.groups.splice(index + 1, 0, group as any);
        index = this.filteredGroups.map(g => g.guid).indexOf(parent.guid);
        this.filteredGroups.splice(index + 1, 0, group);
        this._$rootScope.$broadcast(groupSelected, this.selectedGroup);
        this._$rootScope.$broadcast(groupAdded, group);
        this.bringIntoView(group);
    }

    private updateExisting(itemId: string, group: Group) {
        const movedItemGuid = itemId.replace('group', '');
        if (movedItemGuid !== group.guid) {
            this.moveGroup(group, movedItemGuid);
        } else {
            this._loggerService.logWarning('Cannot add a group on itself');
        }
    }

    private moveGroup(group: Group, movedItemGuid: string) {
        return this._groupService.isChildOrSubChild(group.guid, movedItemGuid)
            .then(result => {
                if (!result) {
                    const found = _.find(this.groups, g => g.guid === movedItemGuid);
                    if (found && !_.some(this.groups, g => g.parentGUID === group.guid &&
                        found.name.toLowerCase() === g.name.toLowerCase())) {
                        found.children = null;
                        found._children = null;
                        found.parent = null;
                        found.parentGUID = group.guid;
                        return this._groupService.updateGroup(found);
                    } else {
                        return this._$q.reject('Name must be unique in ancestor.');
                    }
                } else {
                    this._loggerService.logWarning('Cannot add a parent to one of its children.');
                    return this._$q.reject();
                }
            })
            .then(_groupsDto => {
                this._loggerService.logSuccess('Successfully updated group.');
                this._$rootScope.$broadcast(groupTreeChanged);
            })
            .catch(reason => this._loggerService
                .logError(`Failed to update group : ${reason.message ? reason.message : reason}`));
    }

    addUnderParentInTree(displayedGroups: Group[], parentGuid: string, group: Group) {
        const parentIndex = this.findParentIndex(displayedGroups, parentGuid) + 1;
        if (parentIndex > 0) {
            this.displayedGroups.splice(parentIndex, 0, group);
        }
    }

    findParentIndex(groups: Group[], guid: string): number {
        return groups.map(g => g.guid).indexOf(guid);
    }

    getGroupUniqueName(groups: Group[], name: string): string {
        let foundGroup: Group;
        let iteration: number = 0;
        let uniqueName: string = name;
        do {
            foundGroup = _.find(groups, g => g.name === ((iteration) ? `${name}${iteration}` : name));
            uniqueName = ((iteration) ? `${name}${iteration}` : name);
            iteration++;
        } while (foundGroup);
        return uniqueName;
    }

    checkGroup(groupGuid: string) {
        this.checkDeleteGroup({ groupGuid });
    }
}

export const wtSexyModelTree: ComponentDefinition = {
    componentId: 'wtSexyModelTree',
    controller: SexyModelTreeController,
    template: require('./sexyModelTree.component.html'),
    controllerAs: 'vm',
    bindings: {
        checkDeleteGroup: '&',
        exposedScope: '=',
        filteredGroups: '=?',
        formController: '<?',
        groups: '=',
        hasMore: '=',
        isEditMode: '=',
        isLoading: '<?',
        loadingMessage: '<?',
        menu: '<',
        selectedGroup: '=',
        templateGuid: '=?',
        validationPageConfigurations: '<?'
    }
};